import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "../api/axios";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import CommonTable from "../components/CommonTable";
import { PreferencesContext } from "../context/PreferencesContext";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import ItemDialog from "../components/ItemDialog";
import { genderLabel, variantLabel } from "../utils/uniformConstants";

const defaultForm = {
  name: "",
  description: "",
  price: "",
  productLine: "uniform",
  uniformGroup: "",
  gender: "",
  size: "",
  uniformVariant: "",
  grades: [],
  programs: [],
  subjects: [],
  category: [],
  costMode: "costPrice",
  costPrice: "",
  commissionPercent: "",
};

const UniformCatalog = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [masters, setMasters] = useState({ grades: [], programs: [], subjects: [] });

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemForm, setItemForm] = useState(defaultForm);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    if (organization && academicYear) {
      fetchVendors();
      fetchMasters();
    }
  }, [organization, academicYear]);

  useEffect(() => {
    if (selectedVendor) fetchCatalog();
  }, [selectedVendor]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get("/vendors", {
        params: {
          orgId: organization,
          academicYear,
          vendorType: "uniform",
        },
      });
      const list = res.data || [];
      setVendors(list);
      setSelectedVendor(list[0]?._id || "");
    } catch {
      showMessage("Failed to load uniform vendors", "error");
    }
  };

  const fetchMasters = async () => {
    try {
      const res = await axios.get(`/masters/${organization}`);
      setMasters(res.data);
    } catch {
      showMessage("Failed to load masters", "error");
    }
  };

  const fetchCatalog = async () => {
    try {
      const res = await axios.get("/catalogs", {
        params: { vendorId: selectedVendor, academicYear },
      });
      setCatalog(res.data || { items: [] });
    } catch {
      showMessage("Failed to load catalog", "error");
    }
  };

  const uniformItems = useMemo(
    () =>
      (catalog?.items || []).filter((item) => item.productLine === "uniform"),
    [catalog]
  );

  const saveCatalogLinks = async (items) => {
    const res = await axios.post("/catalogs", {
      vendor: selectedVendor,
      academicYear,
      organization,
      categories: catalog?.categories || [],
      items: items.map((i) => i._id),
      collections: [],
    });
    setCatalog(res.data);
    showMessage("Catalog updated", "success");
    return res.data;
  };

  const ensureCatalog = async () => {
    if (catalog?._id) return catalog;
    const res = await axios.post("/catalogs", {
      vendor: selectedVendor,
      academicYear,
      organization,
      categories: [],
      items: [],
      collections: [],
    });
    setCatalog(res.data);
    return res.data;
  };

  const openItemDialog = (item = null) => {
    if (item) {
      setItemForm({
        ...item,
        productLine: "uniform",
        costMode:
          item.costPrice != null ? "costPrice" : "commissionPercent",
      });
      setEditingItem(item);
    } else {
      setItemForm(defaultForm);
      setEditingItem(null);
    }
    setItemDialogOpen(true);
  };

  const handleItemSave = async () => {
    if (!itemForm.uniformGroup?.trim())
      return showMessage("Product group is required", "warning");
    if (!itemForm.gender)
      return showMessage("Gender is required", "warning");
    if (!itemForm.uniformVariant)
      return showMessage("Variant is required", "warning");
    if (!itemForm.size) return showMessage("Size is required", "warning");
    if (!itemForm.price || itemForm.price <= 0)
      return showMessage("Valid price required", "warning");

    try {
      const cat = catalog?._id ? catalog : await ensureCatalog();
      const payload = {
        ...itemForm,
        productLine: "uniform",
        vendor: selectedVendor,
        academicYear,
        organization,
        catalog: cat._id,
      };
      const saved = editingItem
        ? await axios.put(`/items/${editingItem._id}`, payload)
        : await axios.post("/items", payload);

      const updatedItems = editingItem
        ? uniformItems.map((i) =>
            i._id === saved.data._id ? saved.data : i
          )
        : [...uniformItems, saved.data];

      await saveCatalogLinks(updatedItems);
      setItemDialogOpen(false);
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to save SKU", "error");
    }
  };

  const handleDeleteItem = async (item) => {
    if (
      !(await confirm({
        title: "Delete uniform SKU",
        message: "Remove this SKU from the catalog?",
      }))
    )
      return;
    try {
      await axios.delete(`/items/${item._id}`);
      await saveCatalogLinks(uniformItems.filter((i) => i._id !== item._id));
      showMessage("SKU removed", "success");
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to delete SKU", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Uniform Catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage uniform SKUs (gender, size, variant) for uniform suppliers only.
      </Typography>

      <FormControl sx={{ mb: 2, minWidth: 300 }}>
        <InputLabel>Uniform vendor</InputLabel>
        <Select
          value={selectedVendor}
          onChange={(e) => setSelectedVendor(e.target.value)}
          label="Uniform vendor"
        >
          {vendors.map((v) => (
            <MenuItem key={v._id} value={v._id}>
              {v.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!vendors.length && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No uniform vendors found. Add a vendor with type &quot;Uniform supplier&quot; first.
        </Typography>
      )}

      {selectedVendor && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6">Uniform SKUs</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openItemDialog()}
            >
              Add SKU
            </Button>
          </Box>
          <CommonTable
            columns={[
              { label: "Name", field: "name" },
              { label: "Product", field: "uniformGroup" },
              {
                label: "Gender",
                renderCell: (row) => genderLabel(row.gender),
              },
              {
                label: "Variant",
                renderCell: (row) => variantLabel(row.uniformVariant),
              },
              { label: "Size", field: "size" },
              { label: "Price", field: "price" },
              {
                label: "Grades",
                renderCell: (i) => (i.grades || []).join(", ") || "All",
              },
              {
                label: "Actions",
                renderCell: (item) => (
                  <>
                    <Button size="small" onClick={() => openItemDialog(item)}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDeleteItem(item)}
                    >
                      Delete
                    </Button>
                  </>
                ),
              },
            ]}
            rows={uniformItems}
          />
        </Box>
      )}

      <ItemDialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        onSave={handleItemSave}
        form={itemForm}
        setForm={setItemForm}
        masters={masters}
        categories={[]}
        isEditing={!!editingItem}
        lockProductLine
      />
    </Box>
  );
};

export default UniformCatalog;
